import React, { useEffect, useState } from "react";

import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@formant/ui-kit";

import { DateTimePicker } from "./DateTimePicker";

interface IMuteSettingsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (startDate: Date, durationHours?: number) => void;
    initialMuteData?: {
        start: string;
        end: string;
    } | null;
}

const startTimeOptions = [
    { value: "now", label: "Now" },
    { value: "in_12_hours", label: "In 12 hours" },
    { value: "in_24_hours", label: "In 24 hours" },
    { value: "in_3_days", label: "In 3 days" },
    { value: "in_1_week", label: "In 1 week" },
    { value: "custom", label: "Custom date/time" },
];

const durationOptions = [
    { value: "1", label: "1 hour" },
    { value: "2", label: "2 hours" },
    { value: "3", label: "3 hours" },
    { value: "5", label: "5 hours" },
    { value: "7", label: "7 hours" },
    { value: "11", label: "11 hours" },
    { value: "indefinite", label: "Indefinite" },
];

export const MuteSettingsModal: React.FC<IMuteSettingsModalProps> = ({
    open,
    onOpenChange,
    onSubmit,
    initialMuteData,
}) => {
    const [startTimeOption, setStartTimeOption] = useState("now");
    const [durationOption, setDurationOption] = useState("1");
    const [customDateTime, setCustomDateTime] = useState<Date | undefined>(
        undefined,
    );
    const [customDuration, setCustomDuration] = useState("");

    // Update state when initialMuteData changes
    useEffect(() => {
        if (initialMuteData) {
            setStartTimeOption("custom");
            setDurationOption("custom");
            setCustomDateTime(new Date(initialMuteData.start));
            const durationHours = String(
                (new Date(initialMuteData.end).getTime() -
                    new Date(initialMuteData.start).getTime()) /
                    (1000 * 60 * 60),
            );
            setCustomDuration(durationHours);
        } else {
            // Reset to defaults when initialMuteData is null
            setStartTimeOption("now");
            setDurationOption("1");
            setCustomDateTime(undefined);
            setCustomDuration("");
        }
    }, [initialMuteData]);

    const resetModalState = () => {
        if (initialMuteData) {
            // Restore to initial scheduled values
            setStartTimeOption("custom");
            setDurationOption("custom");
            setCustomDateTime(new Date(initialMuteData.start));
            const durationHours = String(
                (new Date(initialMuteData.end).getTime() -
                    new Date(initialMuteData.start).getTime()) /
                    (1000 * 60 * 60),
            );
            setCustomDuration(durationHours);
        } else {
            // Reset to defaults for new mute
            setStartTimeOption("now");
            setDurationOption("1");
            setCustomDateTime(undefined);
            setCustomDuration("");
        }
    };

    const handleModalOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            resetModalState();
        }
        onOpenChange(isOpen);
    };

    const handleStartTimeChange = (value: string) => {
        setStartTimeOption(value);
    };

    const handleDurationChange = (value: string) => {
        if (!isNaN(Number(value))) {
            if (value === "") {
                setCustomDuration("");
                // Only keep as custom if the select is open
                if (
                    document.activeElement?.closest(
                        "[data-radix-select-content]",
                    )
                ) {
                    setDurationOption("custom");
                } else {
                    setDurationOption("1");
                }
            } else {
                setCustomDuration(value);
                setDurationOption("custom");
            }
        } else {
            setDurationOption(value);
            setCustomDuration(""); // Reset custom duration when selecting predefined option
        }
    };

    const calculateStartDate = (): Date => {
        const now = new Date();
        switch (startTimeOption) {
            case "now":
                return now;
            case "in_12_hours":
                return new Date(now.getTime() + 12 * 60 * 60 * 1000);
            case "in_24_hours":
                return new Date(now.getTime() + 24 * 60 * 60 * 1000);
            case "in_3_days":
                return new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
            case "in_1_week":
                return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            case "custom":
                return customDateTime || now;
            default:
                return now;
        }
    };

    const getDurationHours = (): number => {
        if (durationOption === "indefinite") {
            return 24 * 365 * 100; // 100 years in hours
        }
        if (durationOption === "custom") {
            const duration = parseFloat(customDuration || "1");
            return duration || 1;
        }
        return parseInt(durationOption, 10);
    };

    const formatDuration = (hours: number): string => {
        if (durationOption === "indefinite") {
            return "indefinitely";
        }
        if (hours >= 1) {
            return `${hours} ${hours === 1 ? "hour" : "hours"}`;
        }
        const minutes = Math.round(hours * 60);
        return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    };

    const handleSubmit = () => {
        const startDate = calculateStartDate();
        const durationHours = getDurationHours();
        onSubmit(startDate, durationHours);
        onOpenChange(false);
    };

    const formatSummary = () => {
        const startDate = calculateStartDate();
        const durationHours = getDurationHours();

        const endDate = new Date(
            startDate.getTime() + durationHours * 60 * 60 * 1000,
        );

        const formatTime = (date: Date) => {
            return date.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        };

        const formatDate = (date: Date) => {
            return date.toLocaleDateString([], {
                month: "numeric",
                day: "numeric",
                year: "numeric",
            });
        };

        const startLine = initialMuteData
            ? `Mute is scheduled and will start on ${formatDate(startDate)} at ${formatTime(startDate)} and ${durationOption === "indefinite" ? "continue indefinitely" : `last for ${formatDuration(durationHours)}`}.`
            : `Mute will start on ${formatDate(startDate)} at ${formatTime(startDate)} and ${durationOption === "indefinite" ? "continue indefinitely" : `last for ${formatDuration(durationHours)}`}.`;

        const endLine =
            durationOption === "indefinite"
                ? ""
                : `Mute will end on ${formatDate(endDate)} at ${formatTime(endDate)}.`;

        return {
            startLine,
            endLine,
        };
    };

    return (
        <Dialog open={open} onOpenChange={handleModalOpenChange}>
            <DialogContent
                className="bg-formant-color-background-primary border-formant-color-border p-8"
                onClick={e => e.stopPropagation()}
            >
                <DialogHeader>
                    <DialogTitle>Mute Device</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                    <p className="text-formant-color-text-secondary">
                        Configure when and for how long to mute notifications.
                    </p>

                    <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Select
                            value={startTimeOption}
                            onValueChange={handleStartTimeChange}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select start time" />
                            </SelectTrigger>
                            <SelectContent>
                                {startTimeOptions.map(option => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {startTimeOption === "custom" && (
                            <div className="space-y-4">
                                <DateTimePicker
                                    value={customDateTime}
                                    onChange={setCustomDateTime}
                                />
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Duration</Label>
                        <Select
                            value={durationOption}
                            onValueChange={handleDurationChange}
                        >
                            <SelectTrigger>
                                <SelectValue>
                                    {durationOption === "custom" &&
                                    customDuration
                                        ? `${customDuration} ${Number(customDuration) === 1 ? "hour" : "hours"}`
                                        : durationOptions.find(
                                              opt =>
                                                  opt.value === durationOption,
                                          )?.label || "Select duration"}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <div className="px-2 py-2 border-b border-formant-color-border">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            value={customDuration}
                                            onChange={e => {
                                                const value = e.target.value;
                                                e.stopPropagation();
                                                if (
                                                    !isNaN(Number(value)) &&
                                                    Number(value) >= 0
                                                ) {
                                                    setCustomDuration(value);
                                                    if (
                                                        value !== "" &&
                                                        durationOption !==
                                                            "custom"
                                                    ) {
                                                        setDurationOption(
                                                            "custom",
                                                        );
                                                        setTimeout(
                                                            () =>
                                                                e.target.focus(),
                                                            0,
                                                        );
                                                    }
                                                }
                                            }}
                                            onBlur={() => {
                                                if (customDuration === "") {
                                                    setDurationOption("1");
                                                }
                                            }}
                                            onKeyDown={e => e.stopPropagation()}
                                            placeholder="Enter hours"
                                            className="w-40 px-3 py-2 bg-formant-color-background-secondary border rounded-sm focus:outline-none focus:ring-1 focus:ring-formant-color-primary"
                                            onClick={e => e.stopPropagation()}
                                            min="0"
                                            step="0.01"
                                        />
                                        <span className="text-formant-color-text-secondary">
                                            hours
                                        </span>
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <p className="p-2 text-sm text-formant-color-text-secondary">
                                        Suggested
                                    </p>
                                    {durationOptions.map(option => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                            onMouseDown={e => {
                                                // Prevent focus change when clicking predefined options
                                                if (customDuration) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </div>
                            </SelectContent>
                        </Select>
                    </div>

                    {formatSummary() && (
                        <div className="p-4 bg-formant-color-background-secondary rounded-md">
                            <p className="text-formant-color-text-primary font-bold text-lg mb-2">
                                Summary
                            </p>
                            <div className="space-y-1">
                                <p className="text-formant-color-text-secondary mt-0">
                                    {formatSummary()?.startLine}
                                </p>
                                <p className="text-formant-color-text-secondary mt-0">
                                    {formatSummary()?.endLine}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetModalState();
                                onOpenChange(false);
                            }}
                        >
                            Cancel
                        </Button>
                        {initialMuteData && (
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    onSubmit(new Date(), 0);
                                    onOpenChange(false);
                                }}
                            >
                                Cancel Schedule
                            </Button>
                        )}
                        <Button onClick={handleSubmit}>Confirm</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
